import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { SectionHeader } from '@/components/SectionHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { BookOpen, FolderPlus, Link2, UploadCloud, Search, Loader2, FolderOpen, X } from 'lucide-react';
import { API, authHeader, CATEGORY_CONFIG } from '@/components/library/libraryMeta';
import { LibraryBreadcrumbs } from '@/components/library/LibraryBreadcrumbs';
import { FolderCard } from '@/components/library/FolderCard';
import { ItemCard } from '@/components/library/ItemCard';
import { UploadDialog } from '@/components/library/UploadDialog';
import { FolderDialog } from '@/components/library/FolderDialog';
import { ItemFormDialog } from '@/components/library/ItemFormDialog';
import { PreviewDialog } from '@/components/library/PreviewDialog';

const errMsg = (e, fallback) => e.response?.data?.detail || fallback;
const pathText = (path = []) => path.map((p) => p.name).join(' / ');
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const askDeleteBody = (f) => (f.folder_count || f.item_count
  ? `Everything inside will be removed too (${plural(f.folder_count || 0, 'subfolder')}, ${plural(f.item_count || 0, 'item')} at the first level).`
  : 'This empty folder will be removed.');

const CategoryChips = ({ items, value, onChange }) => (
  <div className="flex gap-2 overflow-x-auto pb-1" data-testid="category-filters">
    {['all', ...Object.keys(CATEGORY_CONFIG)].map((k) => {
      const count = k === 'all' ? items.length : items.filter((i) => (i.category || 'general') === k).length;
      const active = value === k;
      return (
        <Button key={k} size="sm" variant={active ? 'default' : 'outline'} onClick={() => onChange(k)}
          className={`shrink-0 ${active ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white border-0' : 'border-[hsl(var(--border))]'}`}
          data-testid={`category-filter-${k}`}>
          {k === 'all' ? 'All' : CATEGORY_CONFIG[k].label} ({count})
        </Button>
      );
    })}
  </div>
);

const SectionTitle = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--text-muted))] mb-3">{children}</p>
);

const EmptyState = ({ searching, onUpload, onFolder }) => (
  <div className="text-center py-16 rounded-xl border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--card))]" data-testid="library-empty">
    <FolderOpen size={44} className="mx-auto text-cyan-500/30 mb-3" />
    <p className="text-[hsl(var(--text-secondary))] font-medium">{searching ? 'Nothing matches your search' : 'This folder is empty'}</p>
    {!searching && (
      <div className="flex justify-center gap-2 mt-4">
        <Button size="sm" variant="outline" onClick={onFolder} className="border-[hsl(var(--border))]" data-testid="empty-new-folder-btn"><FolderPlus size={14} className="mr-1.5" /> New folder</Button>
        <Button size="sm" onClick={onUpload} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white" data-testid="empty-upload-btn"><UploadCloud size={14} className="mr-1.5" /> Upload files</Button>
      </div>
    )}
  </div>
);

const HeaderActions = ({ onFolder, onLink, onUpload }) => (
  <div className="flex flex-wrap gap-2">
    <Button variant="outline" onClick={onFolder} className="border-[hsl(var(--border))]" data-testid="new-folder-btn"><FolderPlus size={16} className="mr-2" /> New Folder</Button>
    <Button variant="outline" onClick={onLink} className="border-[hsl(var(--border))]" data-testid="add-resource-btn"><Link2 size={16} className="mr-2" /> Add Link</Button>
    <Button onClick={onUpload} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20" data-testid="upload-files-btn"><UploadCloud size={16} className="mr-2" /> Upload Files</Button>
  </div>
);

export default function ResourceLibraryPage() {
  const [params, setParams] = useSearchParams();
  const folderId = params.get('folder') || '';
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState(null);
  const [category, setCategory] = useState('all');
  const [dialog, setDialog] = useState('');
  const [renaming, setRenaming] = useState(null);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [searchTick, setSearchTick] = useState(0);

  const openFolder = useCallback((id) => { setSearch(''); setParams(id ? { folder: id } : {}); }, [setParams]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/library`, { ...authHeader(), params: folderId ? { folder_id: folderId } : {} });
      setData(res.data);
    } catch (e) {
      toast.error(errMsg(e, 'Failed to load library'), { id: 'library-load' });
      if (folderId) openFolder('');
    }
  }, [folderId, openFolder]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = search.trim();
    if (!q) { setResults(null); return undefined; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/library/search`, { ...authHeader(), params: { q } });
        setResults(res.data);
      } catch (e) { toast.error('Search failed'); }
    }, 300);
    return () => clearTimeout(t);
  }, [search, searchTick]);

  const refresh = () => { load(); setSearchTick((n) => n + 1); };
  const label = pathText(data?.breadcrumbs);

  const createFolder = async (name) => {
    try {
      await axios.post(`${API}/library/folders`, { name, parent_id: folderId || null }, authHeader());
      toast.success(`Folder "${name}" created`);
      load();
      return true;
    } catch (e) { toast.error(errMsg(e, 'Failed to create folder')); return false; }
  };

  const renameFolder = async (name) => {
    try {
      await axios.put(`${API}/library/folders/${renaming.id}`, { name }, authHeader());
      toast.success('Folder renamed');
      setRenaming(null);
      load();
      return true;
    } catch (e) { toast.error(errMsg(e, 'Failed to rename folder')); return false; }
  };

  const addLink = async (payload) => {
    try {
      await axios.post(`${API}/library/links`, { ...payload, folder_id: folderId || null }, authHeader());
      toast.success('Link added');
      load();
      return true;
    } catch (e) { toast.error(errMsg(e, 'Failed to add link')); return false; }
  };

  const saveItem = async (payload) => {
    try {
      await axios.put(`${API}/library/items/${editing.id}`, payload, authHeader());
      toast.success('Saved');
      setEditing(null);
      refresh();
      return true;
    } catch (e) { toast.error(errMsg(e, 'Failed to save')); return false; }
  };

  const askDeleteFolder = (f) => setConfirm({
    title: `Delete folder "${f.name}"?`,
    body: askDeleteBody(f),
    run: () => axios.delete(`${API}/library/folders/${f.id}`, authHeader()),
    done: 'Folder deleted',
  });
  const askDeleteItem = (i) => setConfirm({
    title: `Delete "${i.title}"?`, body: 'Team members will no longer be able to view or download it.',
    run: () => axios.delete(`${API}/library/items/${i.id}`, authHeader()), done: 'Item deleted',
  });
  const runConfirm = async () => {
    try { await confirm.run(); toast.success(confirm.done); refresh(); } catch (e) { toast.error(errMsg(e, 'Delete failed')); }
    setConfirm(null);
  };

  if (!data) {
    return <ResponsiveLayout><div className="flex-1 flex items-center justify-center min-h-screen"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" /></div></ResponsiveLayout>;
  }

  const view = results || data;
  const items = view.items.filter((i) => category === 'all' || (i.category || 'general') === category);
  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6" data-testid="library-page">
        <SectionHeader icon={BookOpen} title="UA Resource Library" subtitle="SOPs, templates, guides, files & tools"
          action={<HeaderActions onFolder={() => setDialog('folder')} onLink={() => setDialog('link')} onUpload={() => setDialog('upload')} />} />

        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search all folders and files..."
              className="pl-9 pr-9 bg-[hsl(var(--surface))] border-[hsl(var(--border))]" data-testid="library-search-input" />
            {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" data-testid="library-search-clear"><X size={14} /></button>}
          </div>
          <CategoryChips items={view.items} value={category} onChange={setCategory} />
        </div>

        <div className="mb-5 flex items-center justify-between gap-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-2 py-1.5">
          {results ? <p className="px-2 py-1 text-sm text-[hsl(var(--text-muted))]" data-testid="search-summary">Results for &ldquo;{search.trim()}&rdquo; in all folders</p>
            : <LibraryBreadcrumbs trail={data.breadcrumbs} onOpen={openFolder} />}
        </div>

        {!!view.folders.length && (
          <section className="mb-7" data-testid="folders-section">
            <SectionTitle>Folders ({view.folders.length})</SectionTitle>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {view.folders.map((f) => (
                <FolderCard key={f.id} folder={f} path={results ? pathText(f.path) : ''} onOpen={openFolder}
                  onRename={setRenaming} onDelete={askDeleteFolder} />
              ))}
            </div>
          </section>
        )}

        {items.length ? (
          <section data-testid="items-section">
            <SectionTitle>Files & links ({items.length})</SectionTitle>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((i) => (
                <ItemCard key={i.id} item={i} path={results ? pathText(i.path) : ''} onPreview={setPreview} onEdit={setEditing} onDelete={askDeleteItem} />
              ))}
            </div>
          </section>
        ) : !view.folders.length && <EmptyState searching={!!results} onUpload={() => setDialog('upload')} onFolder={() => setDialog('folder')} />}

        <UploadDialog open={dialog === 'upload'} onOpenChange={(v) => { if (!v) { setDialog(''); load(); } }} folderId={folderId} folderLabel={label} onUploaded={() => setSearchTick((n) => n + 1)} />
        {dialog === 'folder' && <FolderDialog open onOpenChange={(v) => !v && setDialog('')} title="New folder" location={label} onSubmit={createFolder} />}
        {dialog === 'link' && <ItemFormDialog open onOpenChange={(v) => !v && setDialog('')} folderId={folderId} folderLabel={label} onSubmit={addLink} />}
        {renaming && <FolderDialog open onOpenChange={(v) => !v && setRenaming(null)} title="Rename folder" initialName={renaming.name} onSubmit={renameFolder} />}
        {editing && <ItemFormDialog open onOpenChange={(v) => !v && setEditing(null)} item={editing} onSubmit={saveItem} />}
        <PreviewDialog item={preview} onOpenChange={(v) => !v && setPreview(null)} />

        <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
          <AlertDialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="delete-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
              <AlertDialogDescription>{confirm?.body}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={runConfirm} className="bg-red-600 hover:bg-red-700 text-white" data-testid="delete-confirm-btn">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ResponsiveLayout>
  );
}
